"use client";

import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon, FolderIcon } from "@heroicons/react/24/outline";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import Link from "next/link";
import { type ComponentPropsWithoutRef, type Ref, forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import type { SocialProviderAccount } from "../../hooks/useSocialProviderAccounts";
import type { AutoPublishProject } from "../../hooks/useAutoPublishProjects";
import { CompactAccountAvatarStack } from "../AccountsSelectDropdown";
import type { FolderModel } from "../../models/folder_model";
import type { PublicationModel } from "../../types/models";
import Button from "../Button";
import CheckboxInput from "../CheckboxInput";
import { PropertyTileText } from "../PropertyTile";
import Spinner from "../Spinner";
import Typography from "../Typography";
import AutoPostBolt from "./AutoPostBolt";
import AutoPostWaitingStatus from "./AutoPostWaitingStatus";
import { AUTO_POST } from "./autoPostName";
import { AUTO_POST_STATUS, getAutoPostQueueText, autoPublishBusyLabel, autoPostActiveProjectsLabel, getAutoPublishSourceFailure } from "./autoPostStatus";
import { getActiveAutoPublishProjectCount, getCalendarAutoPublishQueues, isAutoPublishProjectBlocked, isAutoPublishProjectWaiting } from "./calendarAutoPublishStatus";

function ProjectThumbnail({ project, className }: { project?: FolderModel; className?: string }) {
  return (
    <span className={twMerge("relative inline-flex aspect-video w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 text-slate-400 dark:bg-surface-secondary", className)} aria-hidden>
      <FolderIcon className="size-4" />
      {project?.thumbnailUrl ? (
        <img
          key={project.thumbnailUrl}
          src={project.thumbnailUrl}
          alt=""
          className="absolute inset-0 size-full object-cover"
          loading="lazy"
          onError={(event) => { event.currentTarget.hidden = true; }}
        />
      ) : null}
    </span>
  );
}

function ProjectStatus({ subtitle, failed, waiting = false }: { subtitle: string; failed: boolean; waiting?: boolean }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;
    let disposed = false;
    const measure = () => {
      if (!disposed) setIsTruncated(element.scrollWidth > element.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    void document.fonts.ready.then(measure);
    return () => { disposed = true; observer.disconnect(); };
  }, [subtitle, waiting]);

  const showTooltip = !waiting && isTruncated;

  return (
    <span
      className={twMerge("flex min-w-0 items-center", failed && "text-red-500")}
      data-tooltip-id={showTooltip ? "global" : undefined}
      data-tooltip-content={showTooltip ? subtitle : undefined}>
      <span ref={textRef} className="truncate">
        {waiting ? <AutoPostWaitingStatus /> : subtitle}
      </span>
    </span>
  );
}

/** The calendar's half of the Schedule / Auto post split button. */
export const CalendarAutoPublishTrigger = forwardRef<HTMLButtonElement, {
  projects: AutoPublishProject[];
  publications?: PublicationModel[];
  loading?: boolean;
  error?: boolean;
  /** Stable owner ref, alongside the ref supplied by the positioning library. */
  buttonRef?: Ref<HTMLButtonElement>;
} & ComponentPropsWithoutRef<"button">>(({ projects, publications, loading = false, error = false, buttonRef, className, ...rest }, ref) => {
  const elementRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => elementRef.current!);
  useImperativeHandle(buttonRef, () => elementRef.current!);
  const activeCount = getActiveAutoPublishProjectCount(projects);
  const queues = useMemo(() => getCalendarAutoPublishQueues(publications), [publications]);
  const waiting = !loading && !error && projects.some(project => isAutoPublishProjectWaiting(project, queues.get(project.id)));
  const label = loading ? AUTO_POST_STATUS.loading : error ? AUTO_POST_STATUS.unavailable : autoPostActiveProjectsLabel(activeCount);
  const expanded = rest["aria-expanded"] === true || rest["aria-expanded"] === "true";

  return (
    <Button
      {...rest}
      ref={elementRef}
      size="m"
      variant="primary"
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={loading || rest.disabled}
      data-tooltip-id="global"
      data-tooltip-hidden={expanded}
      data-tooltip-content={expanded ? undefined : label}
      className={twMerge("group rounded-l-none border-l-0 before:absolute before:inset-y-2 before:left-0 before:w-px before:bg-current before:opacity-25 before:content-['']", activeCount > 0 && "w-auto tabular-nums", loading && "disabled:opacity-100", className)}
    >
      <AutoPostBolt active={activeCount > 0} waiting={waiting} loading={loading} />
      {activeCount > 0 ? (
        <Button.Text>{activeCount}</Button.Text>
      ) : null}
    </Button>
  );
});
CalendarAutoPublishTrigger.displayName = "CalendarAutoPublishTrigger";

export function CalendarAutoPublishProjectList({
  projects,
  publications,
  accounts,
  selectedProjectId,
  focusEdge,
  onSelect,
  onToggle,
  busyProjectId,
  busyStatus,
  isStarting = false,
  disabled = false,
}: {
  projects: AutoPublishProject[];
  publications?: PublicationModel[];
  accounts: SocialProviderAccount[];
  selectedProjectId?: string;
  focusEdge?: { edge: "first" | "last"; request: number } | null;
  onSelect: (folderId: string) => void;
  onToggle: (folderId: string, enabled: boolean) => void;
  busyProjectId?: string | null;
  /** Last confirmed subtitle while a settings save and queue refresh finish. */
  busyStatus?: string;
  isStarting?: boolean;
  disabled?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const metadataId = useId();
  const [focusedProjectId, setFocusedProjectId] = useState<string | null>(null);
  const focusedProjectIndex = projects.findIndex(project => project.id === focusedProjectId);
  const [focusSlot, setFocusSlot] = useState<number | null>(null);
  const virtualizer = useVirtualizer({
    count: projects.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 56,
    getItemKey: (index) => projects[index].id,
    overscan: 5,
    // Keep the keyboard target mounted when the user scrolls with the wheel.
    rangeExtractor: (range) => {
      const visible = defaultRangeExtractor(range);
      return focusedProjectIndex < 0 || visible.includes(focusedProjectIndex)
        ? visible : [...visible, focusedProjectIndex].sort((a, b) => a - b);
    },
  });
  const virtualItems = virtualizer.getVirtualItems();
  const controlsDisabled = disabled || Boolean(busyProjectId);
  const focusableSlots = useMemo(() => projects.flatMap((project, index) =>
    isAutoPublishProjectBlocked(project) ? [index * 2] : [index * 2, index * 2 + 1]), [projects]);
  const lastFocusSlot = focusableSlots[focusableSlots.length - 1] ?? null;

  // Restore the selected project after drilling back, including off-screen rows.
  useEffect(() => {
    if (controlsDisabled) return;
    const index = Math.max(0, projects.findIndex(project => project.id === selectedProjectId));
    setFocusSlot(!projects.length ? null : focusEdge ? focusEdge.edge === "last" ? lastFocusSlot : 0 : index * 2);
    if (!projects.length) rootRef.current?.querySelector<HTMLElement>("button, a")?.focus({ preventScroll: true });
  }, [selectedProjectId, controlsDisabled, projects.length, focusEdge, lastFocusSlot]);

  useEffect(() => {
    if (focusSlot === null || controlsDisabled) return;
    const index = Math.floor(focusSlot / 2);
    virtualizer.scrollToIndex(index, { align: "auto" });
    const row = scrollRef.current?.querySelector(`[data-project-index="${index}"]`);
    const target = (focusSlot % 2 ? row?.querySelector<HTMLElement>("input:not(:disabled)") : null)
      ?? row?.querySelector<HTMLElement>("button");
    if (target) {
      target.focus({ preventScroll: true });
      setFocusSlot(null);
    }
  }, [focusSlot, controlsDisabled, virtualizer, virtualItems]);

  const queues = useMemo(() => getCalendarAutoPublishQueues(publications), [publications]);
  const activeProjectCount = getActiveAutoPublishProjectCount(projects);
  return (
    <div ref={rootRef}>
      <div className="flex items-center gap-1.5 px-4 pb-2 pt-4">
        <Typography variant="subtitle3">{AUTO_POST}</Typography>
        {activeProjectCount > 0 ? (
          <Typography variant="subtitle3" className="!font-normal tabular-nums text-slate-400">
            {activeProjectCount}<span className="sr-only"> active {activeProjectCount === 1 ? "project" : "projects"}</span>
          </Typography>
        ) : null}
      </div>
      {projects.length > 0 ? (
        <div ref={scrollRef} className="max-h-[min(20rem,calc(100dvh-12rem))] overflow-y-auto overscroll-contain p-1"
          onFocusCapture={(event) => {
            const row = (event.target as HTMLElement).closest<HTMLElement>("[data-auto-post-project-id]");
            if (row) setFocusedProjectId(row.dataset.autoPostProjectId ?? null);
          }}
          onKeyDown={(event) => {
            if (controlsDisabled || !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            const target = event.target as HTMLElement;
            const row = target.closest<HTMLElement>("[data-project-index]");
            if (!row) return;
            event.preventDefault();
            event.stopPropagation();
            const slot = Number(row.dataset.projectIndex) * 2 + (target.tagName === "INPUT" ? 1 : 0);
            const current = focusableSlots.indexOf(slot);
            const total = focusableSlots.length;
            const next = event.key === "Home" ? 0 : event.key === "End" ? total - 1
              : (current + (event.key === "ArrowDown" ? 1 : total - 1)) % total;
            setFocusSlot(focusableSlots[next]);
          }}>
        <ul aria-label="Projects" className="relative" style={{ height: virtualizer.getTotalSize() }}>
          {virtualItems.map((item) => {
            const project = projects[item.index];
            const on = project.autoPublish?.enabled === true;
            const blocked = isAutoPublishProjectBlocked(project);
            const selectedAccounts = accounts.filter(account => project.autoPublish?.accountIds.includes(account.id));
            const queue = queues.get(project.id);
            const sourceFailure = getAutoPublishSourceFailure(project.taskStatus);
            const status = busyProjectId === project.id && busyStatus !== undefined ? busyStatus
              : !on ? null : queue ? getAutoPostQueueText(queue)?.summary
              : project.taskStatus === "processing" ? AUTO_POST_STATUS.waitingForShorts
              : project.taskStatus === "ready" ? AUTO_POST_STATUS.empty : null;
            const subtitle = [sourceFailure, status].filter(Boolean).join(" · ");
            const waiting = isAutoPublishProjectWaiting(project, queue) && status === AUTO_POST_STATUS.waitingForShorts;
            const accountNames = selectedAccounts.map(account => account.username || account.name).join(", ");
            return (
              <li key={project.id} data-auto-post-project-id={project.id} data-project-index={item.index}
                aria-posinset={item.index + 1} aria-setsize={projects.length}
                className="group/tile absolute left-0 top-0 flex w-full min-w-0 items-center gap-1 rounded-[14px] hover:bg-slate-150 dark:hover:bg-slate-800/20"
                style={{ height: item.size, transform: `translateY(${item.start}px)` }}>
                <button
                  type="button"
                  disabled={controlsDisabled}
                  onClick={() => onSelect(project.id)}
                  aria-label={`Manage ${AUTO_POST} for ${project.name}`}
                  aria-describedby={`${metadataId}-${project.id}`}
                  className="flex h-full min-w-0 flex-1 items-center gap-3 rounded-[14px] py-2 pl-3 pr-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60"
                >
                  <ProjectThumbnail project={project} className="w-12" />
                  <PropertyTileText component="span" className="min-w-0 flex-1"
                    title={<span className="flex min-w-0 items-center gap-1">
                      <Typography variant="subtitle3" component="span" className="min-w-0 truncate" title={project.name}>{project.name}</Typography>
                      <span className="flex w-5 shrink-0 items-center justify-center" aria-hidden>
                        <ChevronRightIcon className="invisible size-3.5 text-foreground-secondary group-hover/tile:visible group-focus-within/tile:visible" />
                      </span>
                    </span>}
                    subtitle={<span id={`${metadataId}-${project.id}`}>
                      {subtitle ? <ProjectStatus subtitle={subtitle} failed={Boolean(sourceFailure)} waiting={waiting} /> : null}
                      {accountNames ? <span className="sr-only">Accounts: {accountNames}.</span> : null}
                    </span>}
                  />
                  {selectedAccounts.length > 0 ? (
                    <span className="shrink-0" title={accountNames}>
                      <CompactAccountAvatarStack accounts={selectedAccounts} selectedAccountIds={project.autoPublish?.accountIds ?? []}
                        maxAvatars={3} size="xs" ringClassName="ring-background group-hover/tile:ring-slate-150 dark:group-hover/tile:ring-surface-secondary" />
                    </span>
                  ) : null}
                </button>
                <span className="mr-3 flex size-7 shrink-0 items-center justify-center"
                  title={blocked ? AUTO_POST_STATUS.sourceBlocked : undefined}>
                  {busyProjectId === project.id ? (
                    <span role="status" aria-label={autoPublishBusyLabel({ isSaving: true, isStarting }, project.name)}><Spinner className="size-4 text-slate-400" /></span>
                  ) : (
                    <CheckboxInput
                      size="s"
                      label={`${AUTO_POST} for ${project.name}${blocked ? ` · ${AUTO_POST_STATUS.sourceBlocked}` : ""}`}
                      className="[&>p]:sr-only [&_input:focus-visible+div]:ring-2 [&_input:focus-visible+div]:ring-ring"
                      checked={on && !blocked}
                      disabled={controlsDisabled || blocked}
                      onValueChange={(enabled) => { if (!blocked) onToggle(project.id, enabled); }}
                    />
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        </div>
      ) : (
        <div className="px-4 pb-4 pt-3">
          <Typography variant="caption" className="text-slate-500">Your projects will appear here once you create one.</Typography>
          <Button asChild variant="secondary" size="s" className="mt-3"><Link href="/spaces">View projects</Link></Button>
        </div>
      )}
    </div>
  );
}

export function AutoPublishProjectHeader({ project, onBack }: { project?: FolderModel; onBack: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-2 border-b border-border p-2">
      <Button type="button" variant="tertiary" size="m" aria-label="Back to projects" onClick={onBack}><ChevronLeftIcon /></Button>
      <ProjectThumbnail project={project} className="w-10 rounded" />
      <Typography variant="subtitle3" className="min-w-0 flex-1 truncate" title={project?.name}>{project?.name ?? "Project settings"}</Typography>
      {project ? (
        <Button asChild variant="tertiary" size="m" aria-label="Open project"
          data-tooltip-id="global" data-tooltip-content="Open project">
          <Link href={`/spaces/${encodeURIComponent(project.id)}`}><ArrowUpRightIcon /></Link>
        </Button>
      ) : null}
    </div>
  );
}
